import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'src/utils/Hooks';
import { Page, Layout, SidebarTitle } from './styles';
import SummaryTab from '../tabs/SummaryTab';
import TemplatesTab from '../tabs/TemplatesTab';
import DataTab from '../tabs/DataTab';
import AdminTab from '../tabs/AdminTab';
import SidebarTabs from '../SidebarTabs';
import { BEACON_PRO_SIDEBAR_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useUpdateQueryParams } from 'src/utils/Hooks/useUpdateQueryParams';
import RetailersFilters from '../views/RetailersFilters';

const ShelfPage: React.FC = () => {
  const [subtab, setSubtab] = useState<'summary' | 'templates' | 'data' | 'admin'>('summary');
  const [templateStage, setTemplateStage] = useState<'type' | 'inputs' | 'specs' | 'confirm'>('type');
  const [selectedTemplate, setSelectedTemplate] = useState<'reviews' | 'pricing' | 'availability' | 'rank' | 'promotions' | 'content' | 'buybox' | 'llm' | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [inputsProductUrls, setInputsProductUrls] = useState<string[]>([]);
  const [retailerCount, setRetailerCount] = useState<number>(0);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [urlsByRetailerId, setUrlsByRetailerId] = useState<Record<string, string[]>>({});
  // Data Explorer (Retailers) filter state
  const [dataSearch, setDataSearch] = useState('');
  const [dataCountries, setDataCountries] = useState<string[]>([]);
  const [dataIndustries, setDataIndustries] = useState<string[]>([]);
  const [dataFilterOptions, setDataFilterOptions] = useState<{ countries: { id: string; label: string }[]; industries: { id: string; label: string }[] }>({ countries: [], industries: [] });
  const history = useHistory();
  const location = useLocation();
  const updateQuery = useUpdateQueryParams(history);

  const templateTitle = selectedTemplate
    ? (
      {
        pricing: 'Pricing',
        content: 'Content',
        reviews: 'Reviews',
        availability: 'Availability',
        rank: 'Rank',
        promotions: 'Promotions',
        buybox: 'Buy Box',
        llm: 'LLM'
      } as const
    )[selectedTemplate]
    : 'Template';

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Sync subtab state from URL's standard `subtab` param
  useEffect(() => {
    try {
      const search = new URLSearchParams(location.search);
      const t = (search.get('subtab') || '').toLowerCase();
      if (t === 'summary' || t === 'templates' || t === 'data' || t === 'admin') {
        setSubtab(t as any);
      }
    } catch {}
  }, [location.search]);

  // Listen for internal navigation events to Data tab (without URL page indicator)
  useEffect(() => {
    const handler = (e: any) => {
      const batchId = e?.detail?.batchId as string | undefined;
      navigateToDataTab(batchId);
    };
    window.addEventListener('shelf:navigate-data' as any, handler);
    return () => window.removeEventListener('shelf:navigate-data' as any, handler);
  }, []);

  // When returning to the Agents tab, reset to the Types page
  useEffect(() => {
    if (subtab === 'templates') {
      setTemplateStage('type');
    }
  }, [subtab]);

  const navigateToDataTab = (batchId?: string) => {
    updateQuery({ additionalParams: { subtab: 'data', ...(batchId ? { batchId: String(batchId) } : {}) } });
  };

  const handleSetSubtab = (next: 'summary' | 'templates' | 'data' | 'admin') => {
    const additionalParams: Record<string, string | null> = { subtab: next } as any;
    if (next === 'summary') {
      additionalParams.shelfCampaignId = null;
    }
    updateQuery({ additionalParams });
  };

  return (
    <Page>
      <Layout>
        <div style={{ width: BEACON_PRO_SIDEBAR_WIDTH }}>
          <SidebarTitle>Agentic Shelf</SidebarTitle>
          <SidebarTabs subtab={subtab} setSubtab={handleSetSubtab} />
          {subtab === 'data' ? (
            <div style={{ marginTop: 12 }}>
              <RetailersFilters
                search={dataSearch}
                onSearchChange={setDataSearch}
                availableCountries={dataFilterOptions.countries}
                selectedCountries={dataCountries}
                onToggleCountry={(id) =>
                  setDataCountries((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
                }
                availableIndustries={dataFilterOptions.industries}
                selectedIndustries={dataIndustries}
                onToggleIndustry={(id) =>
                  setDataIndustries((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
                }
              />
            </div>
          ) : null}
        </div>
        <div>
          {subtab === 'summary' && (<SummaryTab onNavigateToDataTab={navigateToDataTab} />)}

          {subtab === 'templates' && (
            <TemplatesTab
              templateTitle={templateTitle}
              templateStage={templateStage}
              setTemplateStage={setTemplateStage}
              inputsProductUrls={inputsProductUrls}
              setInputsProductUrls={(urls) => setInputsProductUrls(urls)}
              retailerCount={retailerCount}
              setRetailerCount={(n) => setRetailerCount(n)}
              frequency={frequency}
              setFrequency={(f) => setFrequency(f)}
              startDate={startDate}
              endDate={endDate}
              setStartDate={(v) => setStartDate(v)}
              setEndDate={(v) => setEndDate(v)}
              projectName={projectName}
              setProjectName={(v) => setProjectName(v)}
              onSelectTemplate={(t) => { setSelectedTemplate(t); }}
              urlsByRetailerId={urlsByRetailerId}
              setUrlsByRetailerId={(m) => setUrlsByRetailerId(m)}
            />
          )}

          {subtab === 'data' && (
            <DataTab
              search={dataSearch}
              selectedCountries={dataCountries}
              selectedIndustries={dataIndustries}
              onSearchChange={setDataSearch}
              onToggleCountry={(id) =>
                setDataCountries((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
              }
              onToggleIndustry={(id) =>
                setDataIndustries((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]))
              }
              onOptionsChange={setDataFilterOptions}
            />
          )}

          {/* {subtab === 'admin' && (<AdminTab />)} */}
        </div>
      </Layout>
    </Page>
  );
};

export default ShelfPage;
